/**
 * features/library/api.ts — typed wrappers around /api/tenant/library.
 */

import { z } from 'zod';
import { api } from '@/lib/api';
import {
  JobTemplateCustomizeResponseSchema,
  JobTemplateDeleteResponseSchema,
  JobTemplateDetailResponseSchema,
  JobTemplateMutationResponseSchema,
  JobTemplatesListResponseSchema,
  LibraryCustomizeResponseSchema,
  LibraryDeleteResponseSchema,
  LibraryItemDetailResponseSchema,
  LibraryItemMutationResponseSchema,
  LibraryListResponseSchema,
  type ItemFormValues,
  type JobTemplateCreateBody,
  type JobTemplateCustomizeResponse,
  type JobTemplateDeleteResponse,
  type JobTemplateDetailResponse,
  type JobTemplateMutationResponse,
  type JobTemplatesListResponse,
  type JobTemplateUpdateBody,
  type LibraryCustomizeResponse,
  type LibraryDeleteResponse,
  type LibraryItemDetailResponse,
  type LibraryItemMutationResponse,
  type LibraryKind,
  type LibraryListResponse,
} from './schemas';

function tenantHeaders(tenantId: string): Record<string, string> {
  return { 'X-Tenant-Id': tenantId };
}

function basePath(kind: LibraryKind): string {
  return kind === 'position' ? '/tenant/library/positions' : '/tenant/library/units';
}

export async function listLibrary(
  tenantId: string,
  kind: LibraryKind,
): Promise<LibraryListResponse> {
  return api.get(basePath(kind), LibraryListResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function getLibraryItem(
  tenantId: string,
  kind: LibraryKind,
  id: string,
): Promise<LibraryItemDetailResponse> {
  return api.get(`${basePath(kind)}/${encodeURIComponent(id)}`, LibraryItemDetailResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function createLibraryItem(
  tenantId: string,
  kind: LibraryKind,
  values: ItemFormValues,
): Promise<LibraryItemMutationResponse> {
  const body = {
    name: values.name,
    ...(values.description?.trim() ? { description: values.description } : {}),
  };
  return api.post(basePath(kind), LibraryItemMutationResponseSchema, body, {
    headers: tenantHeaders(tenantId),
  });
}

export async function customizeLibraryItem(
  tenantId: string,
  kind: LibraryKind,
  systemRowId: string,
): Promise<LibraryCustomizeResponse> {
  return api.post(
    `${basePath(kind)}/customize`,
    LibraryCustomizeResponseSchema,
    { systemRowId },
    { headers: tenantHeaders(tenantId) },
  );
}

export async function updateLibraryItem(
  tenantId: string,
  kind: LibraryKind,
  id: string,
  patch: { name?: string; description?: string | null },
): Promise<LibraryItemMutationResponse> {
  return api.patch(
    `${basePath(kind)}/${encodeURIComponent(id)}`,
    LibraryItemMutationResponseSchema,
    patch,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function deleteLibraryItem(
  tenantId: string,
  kind: LibraryKind,
  id: string,
): Promise<LibraryDeleteResponse> {
  return api.delete(`${basePath(kind)}/${encodeURIComponent(id)}`, LibraryDeleteResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function listJobTemplates(tenantId: string): Promise<JobTemplatesListResponse> {
  return api.get('/tenant/library/job-templates', JobTemplatesListResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function getJobTemplate(
  tenantId: string,
  id: string,
): Promise<JobTemplateDetailResponse> {
  return api.get(
    `/tenant/library/job-templates/${encodeURIComponent(id)}`,
    JobTemplateDetailResponseSchema,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function createJobTemplate(
  tenantId: string,
  body: JobTemplateCreateBody,
): Promise<JobTemplateMutationResponse> {
  return api.post('/tenant/library/job-templates', JobTemplateMutationResponseSchema, body, {
    headers: tenantHeaders(tenantId),
  });
}

export async function customizeJobTemplate(
  tenantId: string,
  systemRowId: string,
): Promise<JobTemplateCustomizeResponse> {
  return api.post(
    '/tenant/library/job-templates/customize',
    JobTemplateCustomizeResponseSchema,
    { systemRowId },
    { headers: tenantHeaders(tenantId) },
  );
}

export async function updateJobTemplate(
  tenantId: string,
  id: string,
  patch: JobTemplateUpdateBody,
): Promise<JobTemplateMutationResponse> {
  return api.patch(
    `/tenant/library/job-templates/${encodeURIComponent(id)}`,
    JobTemplateMutationResponseSchema,
    patch,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function deleteJobTemplate(
  tenantId: string,
  id: string,
): Promise<JobTemplateDeleteResponse> {
  return api.delete(
    `/tenant/library/job-templates/${encodeURIComponent(id)}`,
    JobTemplateDeleteResponseSchema,
    { headers: tenantHeaders(tenantId) },
  );
}

// ── Job Template Attachments ─────────────────────────────────────────────────

const TemplateAttachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  mime_type: z.string(),
  size_bytes: z.number(),
  sha256: z.string().optional(),
  created_at: z.string(),
});

const TemplateAttachmentsListResponseSchema = z.object({
  items: z.array(TemplateAttachmentSchema),
});

const TemplateAttachmentResponseSchema = z.object({
  attachment: TemplateAttachmentSchema,
});

export type TemplateAttachment = z.infer<typeof TemplateAttachmentSchema>;

export async function listJobTemplateAttachments(tenantId: string, templateId: string) {
  return api.get(
    `/tenant/library/job-templates/${encodeURIComponent(templateId)}/attachments`,
    TemplateAttachmentsListResponseSchema,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function linkAttachmentToJobTemplate(
  tenantId: string,
  templateId: string,
  attachmentId: string,
) {
  return api.post(
    `/tenant/library/job-templates/${encodeURIComponent(templateId)}/attachments`,
    TemplateAttachmentResponseSchema,
    { attachment_id: attachmentId },
    { headers: tenantHeaders(tenantId) },
  );
}

export async function unlinkAttachmentFromJobTemplate(
  tenantId: string,
  templateId: string,
  attachmentId: string,
) {
  return api.delete(
    `/tenant/library/job-templates/${encodeURIComponent(templateId)}/attachments/${encodeURIComponent(attachmentId)}`,
    z.void(),
    { headers: tenantHeaders(tenantId) },
  );
}
